import { useState } from 'react'

import { sendTestMessage } from 'services/messagingTopics'
import { queueEvent } from 'utilities/analytics'

const REACHED: Record<string, string> = {
    bale: 'رسید: بله ✓',
    eitaa: 'رسید: ایتا ✓',
    sms: 'رسید: پیامک ✓',
}

/** «ساعت ۱۴:۳۰» — when the next test is allowed. */
const clock = (iso?: string) => {
    if (!iso) return ''
    const at = new Date(iso)
    if (Number.isNaN(at.getTime())) return ''
    return at.toLocaleTimeString('fa-IR', {
        hour: '2-digit',
        minute: '2-digit',
    })
}

/**
 * «ارسال آزمایشی» — one message down the same road as every notification:
 * بله first, ایتا if بله does not take it, پیامک if neither does. The answer
 * is where it actually arrived, which is the honest reply to «where do my
 * notifications reach me». Once every two hours; the backend enforces it.
 */
export default function TestSend() {
    const [busy, setBusy] = useState(false)
    const [result, setResult] = useState<string | null>(null)

    const send = async () => {
        if (busy) return
        setBusy(true)
        setResult(null)
        try {
            const answer = await sendTestMessage()
            const tried = answer.attempts.map((attempt) => attempt.channel)
            const reached = answer.channel ? REACHED[answer.channel] : null
            const missed =
                answer.channel && tried[0] && tried[0] !== answer.channel
                    ? `${tried[0] === 'bale' ? 'بله' : 'ایتا'} نرسید؛ `
                    : ''
            setResult(
                reached
                    ? missed + reached
                    : 'به هیچ‌جا نرسید. پیامک و پیام‌رسان‌ها را بالا بررسی کنید.'
            )
            queueEvent('notification_test_sent', {
                channel: answer.channel,
                attempts: tried,
                rate_limited: false,
            })
        } catch (error: any) {
            const next = error?.response?.data?.next_at
            if (error?.response?.status === 429) {
                setResult(
                    `هر دو ساعت یک بار می‌شود پیام آزمایشی فرستاد${
                        next ? `؛ دوباره از ${clock(next)}` : ''
                    }.`
                )
                queueEvent('notification_test_sent', { rate_limited: true })
            } else {
                setResult('ارسال نشد. کمی بعد دوباره امتحان کنید.')
            }
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="mt-3 rounded-md bg-canvas p-3">
            <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-text-secondary leading-5">
                    اول بله، بعد ایتا، بعد پیامک؛ همان راهی که خبرهای واقعی
                    می‌روند. هر دو ساعت یک بار.
                </p>
                <button
                    type="button"
                    onClick={send}
                    disabled={busy}
                    className="shrink-0 rounded-full border border-brand px-3 py-1 text-xs font-bold text-brand-deep disabled:opacity-50"
                >
                    {busy ? 'در حال ارسال…' : 'ارسال آزمایشی'}
                </button>
            </div>
            {result && (
                <p
                    className="mt-2 text-xs font-bold text-brand-deep"
                    aria-live="polite"
                >
                    {result}
                </p>
            )}
        </div>
    )
}
